/**
 * Configuration for test types used in planning and filtering.
 *
 * This configuration defines how different test types are identified by their
 * toetsBusinessKey patterns and provides a centralized place to manage test types.
 *
 * @example Adding a new test type:
 * ```typescript
 * exampleToets: {
 *   label: 'PLANNEN.EXAMPLE_TOETS',
 *   businessKeyPatterns: ['EXT'],
 *   isExclusionList: false
 * }
 * ```
 */
export interface TestTypeConfig {
  /** Translation key for the test type label */
  label: string;
  /**
   * Patterns to match in toetsBusinessKey.
   * If isExclusionList is true, these patterns are excluded instead of included.
   */
  businessKeyPatterns: string[];
  /**
   * If true, the patterns are treated as a blacklist (exclude these patterns).
   * If false, the patterns are treated as a whitelist (include only these patterns).
   * @default false
   */
  isExclusionList?: boolean;
}

export interface TestTypesConfig {
  [key: string]: TestTypeConfig;
}

/**
 * Test type configuration.
 *
 * Order matters for filter display - the order defined here determines
 * the order in which filter options appear in the UI.
 */
export const TEST_TYPES_CONFIG: TestTypesConfig = {
  doorstroomtoets: {
    label: 'FILTERS.DOORSTROOMTOETS',
    businessKeyPatterns: ['PRT', 'SCHR'], // Exclude voorbereidingstoets and schrijfvaardigheidstoets patterns
    isExclusionList: true,
  },
  voorbereidingstoets: {
    label: 'FILTERS.VOORBEREIDINGSTOETS',
    businessKeyPatterns: ['PRT'], // Include only voorbereidingstoets patterns
    isExclusionList: false,
  },
  schrijfvaardigheidstoets: {
    label: 'FILTERS.SCHRIJFVAARDIGHEIDSTOETS',
    businessKeyPatterns: ['SCHR'], // Include only schrijfvaardigheidstoets patterns
    isExclusionList: false,
  },
  // Future test types can be added here:
  // exampleToets: {
  //   label: 'PLANNEN_EXAMPLE_TOETS',
  //   businessKeyPatterns: ['EXT'],
  //   isExclusionList: false,
  // },
};

/**
 * Helper function to check if a toetsBusinessKey matches a test type.
 *
 * @param toetsBusinessKey - The business key to check (can be null/undefined)
 * @param testTypeKey - The key of the test type from TEST_TYPES_CONFIG
 * @returns true if the toetsBusinessKey matches the test type configuration
 */
export function matchesTestType(
  toetsBusinessKey: string | null | undefined,
  testTypeKey: keyof typeof TEST_TYPES_CONFIG,
): boolean {
  const config = TEST_TYPES_CONFIG[testTypeKey];
  if (!config) {
    return false;
  }

  const { businessKeyPatterns, isExclusionList = false } = config;

  // If no business key, treat as not matching any specific type
  if (!toetsBusinessKey) {
    // For exclusion lists (like doorstroomtoets), undefined keys should match
    // since they don't contain the excluded patterns
    return isExclusionList;
  }

  const hasPattern = businessKeyPatterns.some((pattern) =>
    toetsBusinessKey.includes(pattern),
  );

  // If it's an exclusion list, return true when pattern is NOT found
  // If it's an inclusion list, return true when pattern IS found
  return isExclusionList ? !hasPattern : hasPattern;
}

/**
 * Get all test types that a planning item matches.
 *
 * @param toetsBusinessKey - The business key to check
 * @returns Array of test type keys that match
 */
export function getMatchingTestTypes(
  toetsBusinessKey: string | null | undefined,
): string[] {
  return Object.keys(TEST_TYPES_CONFIG).filter((testTypeKey) =>
    matchesTestType(toetsBusinessKey, testTypeKey),
  );
}

/**
 * Get the ordered list of test type keys for display.
 *
 * @returns Array of test type keys in display order
 */
export function getTestTypeKeys(): string[] {
  return Object.keys(TEST_TYPES_CONFIG);
}
